import type { ChatStreamEvent } from '@sophia-ai/shared-types';

/**
 * Parses a server-sent-events response body (from `fetch`, since EventSource is
 * GET-only) into the {@link ChatStreamEvent}s the API emits. Frames are
 * separated by a blank line; only `data:` lines carry the JSON payload.
 * Malformed frames are skipped rather than aborting the stream.
 */
export async function* readSseEvents(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<ChatStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');

      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const event = parseFrame(frame);
        if (event !== null) {
          yield event;
        }

        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): ChatStreamEvent | null {
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('');

  if (data === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(data);

    if (isChatStreamEvent(parsed)) {
      return parsed;
    }
  } catch {
    // Ignore a malformed frame; the next one may be fine.
  }

  return null;
}

function isChatStreamEvent(value: unknown): value is ChatStreamEvent {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false;
  }

  const type = (value as { type: unknown }).type;

  return type === 'delta' || type === 'done' || type === 'error';
}
